import { z } from "zod";
import type {
  CreateExpertPlanRequest,
  ExpertPlan,
  ExpertPlanType,
  UpdateExpertPlanRequest,
} from "@shopexperts/legacy-api";

/**
 * View-model the plan form binds to. Plans are identity-only: pricing lives
 * in billing options (plan-level billing_cycle/price/currency are deprecated
 * compatibility projections the form no longer writes).
 */
export interface ExpertPlanFormValues {
  name: string;
  slug: string;
  expert_type: ExpertPlanType | "";
  is_founding: boolean;
  is_active: boolean;
}

export const EXPERT_PLAN_TYPE_OPTIONS: readonly { label: string; value: ExpertPlanType }[] = [
  { label: "All experts", value: "all" },
  { label: "Freelancer", value: "freelancer" },
  { label: "Agency", value: "agency" },
];

export const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export const expertPlanFormSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, "Plan name is required")
    .max(120, "Keep the plan name under 120 characters"),
  slug: z
    .string()
    .trim()
    .min(1, "Slug is required")
    .max(80, "Keep the slug under 80 characters")
    .regex(
      SLUG_PATTERN,
      "Use lowercase letters, numbers and single hyphens (e.g. freelancer-pro-monthly)",
    ),
  expert_type: z.enum(["freelancer", "agency", "all"], {
    message: "Choose who this plan is for",
  }),
  is_founding: z.boolean(),
  is_active: z.boolean(),
});

export type ExpertPlanFormSchema = typeof expertPlanFormSchema;

export function defaultExpertPlanFormValues(): ExpertPlanFormValues {
  return {
    name: "",
    slug: "",
    expert_type: "",
    is_founding: false,
    is_active: true,
  };
}

export function expertPlanToFormValues(plan: ExpertPlan): ExpertPlanFormValues {
  return {
    name: plan.name,
    slug: plan.slug,
    expert_type: plan.expert_type ?? "",
    is_founding: plan.is_founding,
    is_active: plan.is_active,
  };
}

export function formValuesToCreatePayload(values: ExpertPlanFormValues): CreateExpertPlanRequest {
  if (!values.expert_type) throw new Error("Expert type is required");
  return {
    name: values.name.trim(),
    slug: values.slug.trim(),
    expert_type: values.expert_type,
    is_founding: values.is_founding,
    is_active: values.is_active,
  };
}

export function formValuesToUpdatePayload(values: ExpertPlanFormValues): UpdateExpertPlanRequest {
  if (!values.expert_type) throw new Error("Expert type is required");
  return {
    name: values.name.trim(),
    expert_type: values.expert_type,
    is_founding: values.is_founding,
    is_active: values.is_active,
  };
}

export function formatExpertPlanType(expertType: ExpertPlan["expert_type"]): string {
  if (expertType === "freelancer") return "Freelancer";
  if (expertType === "agency") return "Agency";
  if (expertType === "all") return "All experts";
  return "Untyped";
}

export function describeExpertPlanType(expertType: ExpertPlan["expert_type"]): string {
  if (expertType === "freelancer") return "Available to freelance experts";
  if (expertType === "agency") return "Available to agency experts";
  if (expertType === "all") return "Available to freelancers and agencies";
  return "Not sellable until an expert type is assigned";
}

/**
 * Generate a URL-safe slug from a plan name. Used to seed the slug field
 * on the create form so admins rarely have to type it.
 */
export function suggestSlugFromName(name: string): string {
  return name
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

export function centsToDollars(cents: number): number {
  if (!Number.isFinite(cents)) return 0;
  return Math.round(cents) / 100;
}

export function dollarsToCents(dollars: number): number {
  if (!Number.isFinite(dollars)) return 0;
  return Math.round(dollars * 100);
}

const currencyFormatters = new Map<string, Intl.NumberFormat>();

export function formatPrice(priceCents: number, currency: string): string {
  const safeCurrency = (currency || "USD").toUpperCase();
  const key = safeCurrency;
  let fmt = currencyFormatters.get(key);
  if (!fmt) {
    try {
      fmt = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: safeCurrency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    } catch {
      fmt = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    }
    currencyFormatters.set(key, fmt);
  }
  return fmt.format(centsToDollars(priceCents));
}
